import {
  animate,
  query,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { debounceTime, filter, map } from 'rxjs/operators';
import {
  CourseModel,
  PaginationResponse,
  SearchQuery,
} from 'src/app/api/models';
import { PublicService } from 'src/app/api/services';
import { SidebarActions } from './sidebar/sidebar.actions';
import { AppState } from './app.types';
import { Store } from '@ngrx/store';
import { AuthService } from './auth/auth.service';
import { Router, RouterLink } from '@angular/router';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import {
  EmbedDialogComponent,
  EmbedDialogData,
} from './cms/panel/embed-dialog.component';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DynamicCourseStyleService } from './styles/dynamic-course-style.service';
import { getPinWithEdition, isWebClient } from './utility/app.utils';
import { InfiniteScrollDirective } from './cms/common/infinite-scroll.directive';

import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { PinInputComponent } from './pin-input.component';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { ButtonComponent } from './shared/components/button/button.component';

@Component({
  selector: 'qa-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  animations: [
    trigger('courseCards', [
      transition('-1 => *, 0 => *', [
        style({
          opacity: 0,
          transform: 'translateY(100px)',
        }),
        animate(
          '200ms cubic-bezier(0.23, 1, 0.32, 1)',
          style({
            opacity: 1,
            transform: 'translateY(0)',
          })
        ),
      ]),
      transition('* => 0', [
        animate(
          '200ms cubic-bezier(0.23, 1, 0.32, 1)',
          style({
            opacity: 0,
            transform: 'translateY(100px)',
          })
        ),
      ]),
      transition(':increment', [
        query(
          ':enter',
          [
            style({
              height: 0,
              opacity: 0,
            }),
            animate(
              '200ms cubic-bezier(0.23, 1, 0.32, 1)',
              style({
                height: '*',
                opacity: 1,
              })
            ),
          ],
          { optional: true }
        ),
      ]),
      transition(':decrement', [
        query(
          ':leave',
          [
            animate(
              '200ms cubic-bezier(0.23, 1, 0.32, 1)',
              style({
                height: 0,
                opacity: 0,
              })
            ),
          ],
          { optional: true }
        ),
      ]),
    ]),
  ],
  imports: [
    InfiniteScrollDirective,
    MatIcon,
    MatButton,
    PinInputComponent,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatIconButton,
    MatSuffix,
    RouterLink,
    TranslateModule,
    ButtonComponent,
  ],
})
export class HomeComponent implements OnInit {
  courses: CourseModel[] = [];
  courseSearchControl = new UntypedFormControl();

  appLinks = {
    android: environment.playStoreLink,
    apple: environment.appStoreLink,
  };

  pagination: PaginationResponse;
  loading = false;
  apiUrls: string[];
  possibleEditions: string[];
  isWebClient = isWebClient();

  constructor(
    private courseStyle: DynamicCourseStyleService,
    public matSnackBar: MatSnackBar,
    private store: Store<AppState>,
    private translator: TranslateService,
    private api: PublicService,
    private http: HttpClient,
    public auth: AuthService,
    private router: Router,
    private matDialog: MatDialog
  ) {}

  ngOnInit() {
    this.setAllApiUrl();
    this.courseStyle.applyFrom(null);
    this.courseSearchControl.valueChanges
      .pipe(
        debounceTime(200),
        filter((input: string) => {
          if (input && input.length >= 3) {
            return true;
          }
          this.resetPagination();
          return false;
        })
      )
      .subscribe(() => {});
  }

  setAllApiUrl() {
    this.apiUrls = [
      'https://api.quizacademy.io/business-nest/public/search',
      'https://api.quizacademy.io/school-nest/public/search',
      'https://api.quizacademy.io/university-nest/public/search',
    ];
    this.possibleEditions = ['B', 'S', 'U'];
  }

  resetPagination() {
    this.setAllApiUrl();
    this.pagination = null;
    this.courses = [];
  }

  getCourseId(_: any, item: CourseModel): number {
    return item.id;
  }

  openSideBar() {
    this.store.dispatch(SidebarActions.showSidebar());
  }

  searchEntity() {
    this.resetPagination();
    this.submit();
  }

  submit() {
    const input = this.courseSearchControl.value;
    if (input && input.length >= 3) {
      this.loading = true;
      const query = {
        filters: {
          content_type: 'COURSE',
          search_term: input,
        },
      } as Partial<SearchQuery> as SearchQuery;
      if (this.pagination) {
        query.paging = {
          page: this.pagination.page,
          page_size: this.pagination.page_size,
        };
      }
      let request$: Observable<any> = this.getSearchData(query);
      if (!this.auth.isLoggedIn() && environment.production) {
        request$ = this.getMultiEditionData(query);
      }
      request$.subscribe((response) => {
        if (!this.pagination && !response?.data.length) {
          this.matSnackBar.open(this.translator.instant('noData'));
        }
        this.courses = [...this.courses, ...response.data];
        this.pagination = response.pagination;
        this.loading = false;
      });
    } else {
      this.resetPagination();
      return false;
    }
    return true;
  }

  getSearchData(query: SearchQuery) {
    return this.api.getPublicSearch({ body: query }).pipe(
      map((response) => {
        response.data.forEach((eachData: any) => {
          eachData.pin_code = `${getPinWithEdition(eachData.pin_code)}`;
        });
        return response;
      })
    );
  }

  /**
   * This function calls 3  APIs of 3 different environments together using forkjoin.
   * And once the response is received we are handling pagination of individual environment api response.
   * Along with that we are also setting pin for each environment.
   */
  getMultiEditionData(query: SearchQuery) {
    const requests = this.apiUrls.map((url) => {
      return this.http.post(url, query);
    });
    return forkJoin(requests).pipe(
      map((response) => {
        const searchData = response.reduce((max: any, obj: any) => {
          return max.pagination.total > obj.pagination.total ? max : obj;
        }, response[0]);
        let data = [];
        const indexes = [];
        response.forEach((r: any, index) => {
          r.data.forEach((eachData) => {
            eachData.pin_code =
              this.possibleEditions[index] + `-${eachData.pin_code}`;
          });
          if (!r.pagination.has_next_page) {
            indexes.push(index);
          }
          data = [...data, ...r.data];
        });
        this.apiUrls = this.apiUrls.filter(
          (_, index) => !indexes.includes(index)
        );
        this.possibleEditions = this.possibleEditions.filter(
          (_, index) => !indexes.includes(index)
        );
        searchData.data = data;
        return searchData;
      })
    );
  }

  jumpToLogin(): void {
    this.router.navigateByUrl('/login');
  }

  embedHome(): void {
    this.matDialog.open<unknown, EmbedDialogData>(EmbedDialogComponent, {
      data: {} as EmbedDialogData,
    });
  }

  onScroll() {
    if (this.pagination && this.pagination.has_next_page && !this.loading) {
      this.pagination.page++;
      this.submit();
    }
  }
}
